/**
 * The single fetch every generated hook goes through.
 *
 * `credentials: 'include'` is the whole point: Better Auth authenticates by
 * session cookie, and the dev proxy / nginx keep the API on the same origin so
 * that cookie stays first-party.
 *
 * Lives outside `src/api/` because Orval cleans that directory on every run.
 */
export const apiFetch = async <T>(url: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...lowercaseKeys(init?.headers),
    },
  });

  if (!response.ok) {
    // Thrown, not returned: React Query decides what a failure means, and a
    // silently-empty result would render as "you ate nothing today".
    throw new ApiError(response.status, await readMessage(response));
  }

  // 204 and friends have no body to parse.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return (await response.json()) as T;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Header names are case-insensitive, but the spread merge in `apiFetch` is
 * case-sensitive: the generated hooks send `Content-Type` while this file
 * defaults to lowercase `content-type`, so both survive the merge and the
 * browser concatenates them into `application/json, application/json`, which
 * the server's JSON parser rejects. Lowercasing here makes the explicit header
 * win instead.
 */
function lowercaseKeys(headers?: HeadersInit): Record<string, string> {
  if (!headers) return {};
  const record = headers instanceof Headers ? Object.fromEntries(headers) : { ...headers };
  return Object.fromEntries(
    Object.entries(record).map(([name, value]) => [name.toLowerCase(), String(value)]),
  );
}

async function readMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: unknown };
    const message = body?.message;
    if (typeof message === 'string') return message;
    if (Array.isArray(message)) return message.join(', ');
  } catch {
    // Non-JSON error body; the status alone has to do.
  }
  return `Request failed with status ${response.status}`;
}
